import { Link } from "react-router-dom";
import type { Episode } from "@/mocks/episodes";
import StarRating from "@/components/base/StarRating";
import Tag from "@/components/base/Tag";

interface EpisodeCardProps {
  episode: Episode;
  variant?: "vertical" | "horizontal";
}

export default function EpisodeCard({ episode, variant = "vertical" }: EpisodeCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/${episode.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-600 transition-colors cursor-pointer"
      >
        <div className="relative w-24 h-32 sm:w-28 sm:h-40 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={episode.cover}
            alt={`美国式禁忌2 第${episode.number}集 ${episode.title} 剧照`}
            title={`剧集${episode.number} ${episode.title} - 美国式禁忌2剧情介绍`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-medium text-accent-400">第 {episode.number} 集</span>
            <span className="text-xs text-foreground-500">{episode.date}</span>
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-accent-300 transition-colors truncate">
            {episode.title}
          </h3>
          <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
            {episode.summary}
          </p>
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {episode.tags.slice(0, 2).map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </div>
            <StarRating rating={episode.rating / 2} showValue={false} />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${episode.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-600 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={episode.cover}
          alt={`美国式禁忌2 第${episode.number}集 ${episode.title} 剧照`}
          title={`剧集${episode.number} ${episode.title} - 美国式禁忌2剧情介绍`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 h-7 px-2.5 flex items-center rounded-md bg-background-50/85 backdrop-blur text-xs font-semibold text-foreground-950">
          第 {episode.number} 集
        </span>
        <span className="absolute top-3 right-3 h-7 px-2.5 flex items-center gap-1 rounded-md bg-primary-600/90 backdrop-blur text-xs font-semibold text-background-50">
          <i className="ri-star-fill text-accent-300" />
          {episode.rating.toFixed(1)}
        </span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-accent-300 transition-colors">
          {episode.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
          {episode.summary}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between gap-2">
          <span className="text-xs text-foreground-500">{episode.date}</span>
          <span className="text-xs text-accent-400 flex items-center gap-1 font-medium">
            查看解析
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
}